import {
  useSetApplicationStepAnswerMutation,
  NextStepActionResponse,
  useGetAnalyticsEventDataByEventTypeQuery,
  useUpdateStepAnswerCyberSecurityAuditLogMutation,
  Validation,
} from "pages/application/applicationService";
import MultipleInput from "pages/application/templates/MultipleInput";
import SingleChoice from "pages/application/templates/SingleChoice";
import SingleChoiceWithFollowUp from "pages/application/templates/SingleChoiceWithFollowUp";
import SingleInput from "pages/application/templates/SingleInput";
import SingleChoiceWithData from "pages/application/templates/SingleChoiceWithData";
import { useSelector } from "react-redux";
import Informational from "../templates/Informational";
import MultipleSelection from "../templates/MultipleSelection";
import { Button } from "components/ui/button/Button";
import { DISPLAY_STEP_CODE, VOA_StepCodes, VOI_StepCodes } from "utilities/Constant";
import { getStepData } from "pages/application/question/questionSlice";
import ToastMessage from "components/ui/toast-message/ToastMessage";
import { getCurrentUserApplicationId } from "features/authSlice";
import Verification from "pages/online-verification/Verification";
import { trackFSStepCompleted, trackFSSummaryEvent, analyticsSections } from "utilities/full-story";
import { useEffect } from "react";

const Constants = {
  SingleInput: "singleInput",
  SingleChoice: "singleChoice",
  SingleChoiceWithFollowUp: "singleChoiceWithFollowUp",
  MultipleInput: "multipleInput",
  SingleChoiceWithData: "singleChoiceWithData",
  Informational: "informational",
  MultipleSelection: "multipleSelection",
};

const getStepComponent = (stepType: string, stepCode: string) => {
  if (stepCode === "B15") return SingleChoiceWithFollowUp;
  if (VOA_StepCodes.includes(stepCode) || VOI_StepCodes.includes(stepCode)) return Verification;
  switch (stepType) {
    case Constants.SingleInput:
      return SingleInput;
    case Constants.SingleChoice:
      return SingleChoice;
    case Constants.SingleChoiceWithFollowUp:
      return SingleChoiceWithFollowUp;
    case Constants.MultipleInput:
      return MultipleInput;
    case Constants.SingleChoiceWithData:
      return SingleChoiceWithData;
    case Constants.Informational:
      return Informational;
    case Constants.MultipleSelection:
      return MultipleSelection;
    default:
      return () => null;
  }
};

const shouldShowValidationToast = (validation: Validation): boolean => {
  if (validation.success || !validation.message) {
    return false;
  }
  return true;
};

const Question = () => {
  const stepData = useSelector(getStepData);
  const applicationIdForAnalytics = useSelector(getCurrentUserApplicationId);
  const analyticsSection = analyticsSections.find((s) => s.section === stepData.eventRequiringDataAnalytics)!;
  const eventType = analyticsSection?.eventType;

  const { data: analyticsData } = useGetAnalyticsEventDataByEventTypeQuery(
    { applicationId: applicationIdForAnalytics, eventType },
    { skip: !stepData.eventRequiringDataAnalytics }
  );

  useEffect(() => {
    if (analyticsData) trackFSSummaryEvent(analyticsSection.displayName, analyticsData);
  }, [analyticsData, analyticsSection]);

  const { stepCodeText: stepCode, stepType, label: stepLabel, subLabel: stepLabelSubText } = stepData;

  const StepComponent = getStepComponent(stepType, stepCode);
  const [_, { validation }] = useSetApplicationStepAnswerMutation({
    fixedCacheKey: "updateApplication",
    selectFromResult: (result) => ({
      validation: result.data?.validation,
    }),
  });

  return (
    <>
      <main className="application-main">
        {DISPLAY_STEP_CODE && <h4>{stepCode} :</h4>}
        {validation && shouldShowValidationToast(validation) && (
          <ToastMessage message={validation.message} timeout={3000} position="top-right" state="error" />
        )}
        <div className="questionContainer">
          <div className="application-main__question">
            <h3 className="application-main__question--label">{stepLabel}</h3>
            {stepLabelSubText && <p className="application-main__question--subtext">{stepLabelSubText}</p>}
          </div>
        </div>
      </main>
      <div className="stepsContainer">
        <div className="application-steps">
          <StepComponent />
        </div>
      </div>
    </>
  );
};

export const SkipButton = ({
  skipAction,
  skipActionHandler,
}: {
  skipAction: NextStepActionResponse;
  skipActionHandler?: () => void;
}) => {
  const [skipQuestion] = useSetApplicationStepAnswerMutation({
    fixedCacheKey: "updateApplication",
  });
  const [updateCyberSecurityAuditLog] = useUpdateStepAnswerCyberSecurityAuditLogMutation();
  const applicationId = useSelector(getCurrentUserApplicationId);
  const stepData = useSelector(getStepData);
  const { stepCodeText: stepCode, label: stepLabel } = stepData;
  const handleSkip = async () => {
    if (skipActionHandler) {
      skipActionHandler();
      return;
    }
    await skipQuestion({
      id: applicationId,
      stepActionId: skipAction.stepActionId,
    });
    trackFSStepCompleted(stepCode, stepLabel, true);
    void updateCyberSecurityAuditLog(applicationId);
  };
  return (
    <div className="skip-button-link">
      <Button onClick={() => void handleSkip()} title={skipAction.label} type="link" />
    </div>
  );
};

export default Question;
